import { API } from "@/api";
import ProductFields from "@/components/ProductFields";
import { Loader2, Crop, Trash2, RefreshCw, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

const BUSY = { checking: "Checking photo…", enhancing: "Enhancing with AI…", suggesting: "AI is suggesting details…", approving: "Saving…" };

function Thumb({ item }) {
  const src = item.useEnhanced && item.intake?.enhanced ? `${API}/files/${item.intake.enhanced}` : item.preview;
  return <img src={src} alt="" className="h-20 w-20 rounded-xl object-cover border border-amber-500/20 shrink-0" />;
}

function VersionPicker({ item, update }) {
  if (!item.intake?.enhanced) return null;
  const opt = (v, label, src) => (
    <button type="button" onClick={() => update({ useEnhanced: v })} className={`flex-1 rounded-xl overflow-hidden border-2 ${item.useEnhanced === v ? "border-amber-400" : "border-transparent"}`} data-testid={`intake-${item.key}-use-${v ? "enhanced" : "original"}`}>
      <img src={src} alt={label} className="w-full aspect-square object-cover" />
      <div className="text-[10px] py-1 text-slate-300">{label}</div>
    </button>
  );
  return <div className="flex gap-2 mt-3 text-center">{opt(false, "Original", item.preview)}{opt(true, "AI enhanced", `${API}/files/${item.intake.enhanced}`)}</div>;
}

function Flags({ item, update }) {
  const flags = item.form?.needs_confirmation || [];
  if (!flags.length) return null;
  const toggle = (f) => update({ confirmed: item.confirmed.includes(f) ? item.confirmed.filter((x) => x !== f) : [...item.confirmed, f] });
  return (
    <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" data-testid={`intake-${item.key}-flags`}>
      <div className="text-[11px] font-semibold text-amber-300 flex items-center gap-1"><AlertTriangle size={12} /> Please confirm before approving</div>
      {flags.map((f, i) => (
        <label key={f} className="flex items-start gap-2 text-[11px] text-slate-300 mt-1.5">
          <input type="checkbox" checked={item.confirmed.includes(f)} onChange={() => toggle(f)} className="mt-0.5 accent-[#C6A15B]" data-testid={`intake-${item.key}-flag-${i}`} />
          <span>{f} — I've checked the details above are accurate</span>
        </label>
      ))}
    </div>
  );
}

export default function IntakeItem({ item, tax, attach, update, onCrop, onRemove, onProcess, onApprove }) {
  const busy = BUSY[item.state];
  const pending = (item.form?.needs_confirmation || []).filter((f) => !item.confirmed.includes(f)).length;
  return (
    <div className="glass rounded-2xl p-3" data-testid={`intake-item-${item.key}`}>
      <div className="flex gap-3">
        <Thumb item={item} />
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-300 truncate">{item.file.name}</div>
          {busy && <div className="text-[11px] text-amber-300 mt-1 flex items-center gap-1" data-testid={`intake-${item.key}-busy`}><Loader2 size={12} className="animate-spin" /> {busy}</div>}
          {item.state === "rejected" && <div className="text-[11px] text-red-400 mt-1" data-testid={`intake-${item.key}-rejected`}>{item.error}</div>}
          {item.state === "error" && <div className="text-[11px] text-red-400 mt-1" data-testid={`intake-${item.key}-error`}>{item.error}</div>}
          {item.state === "approved" && <div className="text-[11px] text-emerald-500 mt-1 flex items-center gap-1" data-testid={`intake-${item.key}-approved`}><CheckCircle2 size={12} /> {attach ? "Added to product" : "Saved to My Products"}</div>}
          {item.state === "review" && item.form?.ai && <div className="text-[10px] text-amber-300 mt-1 flex items-center gap-1"><Sparkles size={11} /> AI suggestions — review & correct</div>}
          {item.state === "ready" && (
            <label className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-1.5">
              <input type="checkbox" checked={item.enhance} onChange={(e) => update({ enhance: e.target.checked })} className="accent-[#C6A15B]" data-testid={`intake-${item.key}-enhance`} /> AI enhance this photo
            </label>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.state === "ready" && <button onClick={onCrop} className="text-[11px] px-2 py-1 rounded-lg glass border border-amber-500/20 text-slate-300 flex items-center gap-1" data-testid={`intake-${item.key}-crop`}><Crop size={11} /> Crop / rotate</button>}
            {item.state === "ready" && <button onClick={onProcess} className="text-[11px] px-2 py-1 rounded-lg btn-gold" data-testid={`intake-${item.key}-process`}>Process</button>}
            {item.state === "error" && <button onClick={onProcess} className="text-[11px] px-2 py-1 rounded-lg btn-gold flex items-center gap-1" data-testid={`intake-${item.key}-retry`}><RefreshCw size={11} /> Retry</button>}
            {item.state !== "approved" && !busy && <button onClick={onRemove} className="text-[11px] px-2 py-1 rounded-lg glass text-red-400 flex items-center gap-1" data-testid={`intake-${item.key}-remove`}><Trash2 size={11} /> Remove</button>}
          </div>
        </div>
      </div>
      {item.state === "review" && (
        <div className="mt-2">
          <VersionPicker item={item} update={update} />
          {!attach && <div className="mt-3"><ProductFields value={item.form} onChange={(form) => update({ form })} tax={tax} prefix={`intake-${item.key}`} /></div>}
          {!attach && <Flags item={item} update={update} />}
          <button disabled={pending > 0} onClick={onApprove} className="w-full btn-gold py-2.5 rounded-xl mt-3 text-sm disabled:opacity-50" data-testid={`intake-${item.key}-approve`}>
            {pending > 0 ? `Confirm ${pending} flagged detail(s)` : attach ? "Approve & add photo" : "Approve into My Products"}
          </button>
        </div>
      )}
    </div>
  );
}
